import React from 'react';
import PropTypes from 'prop-types';

const LeaseApplication = ({ data,signatureData, currentDate }) => {
  // Extract data from props
  const {
    medicalLegal,
    personalContact,
    residencySponsor,
    belongingsDevices
  } = data;

  // Reusable form field component with value
  const FormField = ({ label, value = '', width = 'auto', ...props }) => (
    <div style={{ width, justifyContent: 'flex-start', alignItems: 'flex-end', gap: 4, display: 'flex' }}>
      <div style={styles.fieldLabel}>{label}</div>
      <div style={{ flex: '1 1 0', height: 0, borderBottom: '1px black solid', position: 'relative' }}>
        <span style={{ position: 'absolute', bottom: -4, left: 4 }}>{value}</span>
      </div>
    </div>
  );

  // Reusable checkbox component with checked state
  const CheckboxField = ({ label, checked = false }) => (
    <div style={{ justifyContent: 'center', alignItems: 'center', gap: 8, display: 'flex' }}>
      <div style={styles.checkboxLabel}>{label}</div>
      <div style={{ ...styles.checkbox, backgroundColor: checked ? 'black' : 'white' }} />
    </div>
  );

  // Reusable section header
  const SectionHeader = ({ title }) => (
    <div style={styles.sectionHeader}>
      {title}
    </div>
  );

  // Signature field component
  const SignatureField = ({ label, width = 'auto' }) => (
    <div style={{ width, flexDirection: 'column', justifyContent: 'flex-start', alignItems: 'flex-start', gap: 4, display: 'inline-flex' }}>
      <div style={{ alignSelf: 'stretch', height: 40, borderBottom: '1px black solid' }} />
      <div style={styles.signatureLabel}>{label}</div>
    </div>
  );

  // Table row generator with data
  const TableRow = ({ columns, data = [], rows = 1 }) => {
    return (
      <table style={{ width: '100%', borderCollapse: 'collapse' }}>
        <thead>
          <tr>
            {columns.map((col, i) => (
              <th
                key={`header-${i}`}
                style={{
                  border: '1px solid #000',
                  padding: '8px',
                  fontWeight: 700,
                  textTransform: col.uppercase ? 'uppercase' : 'none',
                  textAlign: 'left',
                }}
              >
                {col.label}
              </th>
            ))}
          </tr>
        </thead>
        <tbody>
          {Array.from({ length: rows }).map((_, rowIndex) => (
            <tr key={`row-${rowIndex}`}>
              {columns.map((col, colIndex) => (
                <td
                  key={`cell-${rowIndex}-${colIndex}`}
                  style={{
                    border: '1px solid #000',
                    padding: '8px',
                    height: '30px',
                  }}
                >
                  {data[rowIndex]?.[col.key] || ''}
                </td>
              ))}
            </tr>
          ))}
        </tbody>
      </table>
    );
  };

  return (
    <div style={styles.container}>
      {/* LEASE APPLICATION SECTION */}
      <div style={styles.section}>
        <SectionHeader title="LEASE APPLICATION - APPLICATION FOR RESIDENCE" />
        
        <div style={styles.formGroup}>
          <div style={styles.formRow}>
            <FormField 
              label="Name:" 
              value={`${personalContact.firstName || ''} ${personalContact.lastName || ''}`} 
              width="100%" 
            />
            <FormField label="DOB:" value={personalContact.dob} width="100%" />
            <FormField label="Phone#:" value={personalContact.phone} width="100%" />
          </div>
          
          <div style={styles.fullWidthField}>
            <FormField 
              label="Home address:" 
              value={`${personalContact.address || ''}, ${personalContact.city || ''}, ${personalContact.state || ''} ${personalContact.zip || ''}`} 
              width="100%" 
            />
          </div>
          
          <div style={styles.formRow}>
            <FormField 
              label="Last 4 #'s of Social Security #:" 
              value={personalContact.ssnLast4} 
              width="100%" 
            />
            <FormField 
              label="Sobriety Date:" 
              value={medicalLegal.sobrietyDate} 
              width="100%" 
            />
          </div>
          
          <div style={styles.formRow}>
            <FormField label="DL STATE:" width="100%" value={personalContact.DLSTATE}/>
            <FormField label="DL NUMBER:" width="100%"   value={personalContact.DLNUMBER} />
          </div>
        </div>
      </div>
      
      {/* EMERGENCY CONTACT SECTION */}
      <div style={styles.section}>
        <SectionHeader title="EMERGENCY CONTACT INFORMATION" />
        
        <div style={styles.formGroup}>
          <div style={styles.formRow}>
            <FormField 
              label="Name:" 
              value={personalContact.emergencyContact?.name} 
              width="100%" 
            />
            <FormField 
              label="Relationship:" 
              value={personalContact.emergencyContact?.relationship} 
              width="100%" 
            />
          </div>
          
          <div style={styles.formRow}>
            <FormField 
              label="Phone:" 
              value={personalContact.emergencyContact?.phone} 
              width="100%" 
            />
            <FormField 
              label="Email:" 
              value={personalContact.emergencyContact?.email} 
              width="100%" 
            />
          </div>
        </div>
      </div>
      
      {/* PERSONAL INFORMATION SECTION */}
      <div style={styles.section}>
        <SectionHeader title="PERSONAL INFORMATION" />
        
        <div style={styles.formGroup}>
          <div style={styles.formRow}>
            <FormField 
              label="Marital Status:" 
              value={personalContact.maritalStatus} 
              width="100%" 
            />
            <div style={{ flex: '1 1 0', justifyContent: 'flex-start', alignItems: 'flex-end', gap: 16, display: 'flex' }}>
              <div style={styles.fieldLabel}>Children?</div>
              <div style={styles.checkboxGroup}>
                <CheckboxField label="Yes" checked={personalContact.hasChildren} />
                <CheckboxField label="No" checked={!personalContact.hasChildren} />
              </div>
            </div>
          </div>
        </div>
        
        {/* Children Table */}
        <div style={{ width: 547 }}>
          <TableRow 
            columns={[
              { label: 'NAME(S)', key: 'name', flex: '1 1 0' },
              { label: 'AGE', key: 'age', flex: '1 1 0' }
            ]}
            rows={5}
            data={[
              { name: personalContact?.children?.[0]?.name, age: personalContact?.children?.[0]?.age },
              { name: personalContact?.children?.[1]?.name, age: personalContact?.children?.[1]?.age },
              { name: personalContact?.children?.[2]?.name, age: personalContact?.children?.[2]?.age },
              { name: personalContact?.children?.[3]?.name, age: personalContact?.children?.[3]?.age },
              { name: personalContact?.children?.[4]?.name, age: personalContact?.children?.[4]?.age },
            ]}
          />
        </div>
        
        {/* Employment Information */}
        <div style={styles.formRow}>
          <div style={{ justifyContent: 'flex-start', alignItems: 'flex-end', gap: 16, display: 'flex' }}>
            <div style={styles.fieldLabel}>Are you currently employed?</div>
            <div style={styles.checkboxGroup}>
              <CheckboxField label="Yes" checked={personalContact.isEmployed} />
              <CheckboxField label="No" checked={!personalContact.isEmployed} />
            </div>
          </div>
          <FormField label="Full Time:" width="100%" value={personalContact.employmentType === "Full-time" ? "Yes" : ""}/>
          <FormField label="Part Time:" width="100%" value={personalContact.employmentType === "Part-time" ? "Yes" : ""}/>
        </div>
        
        {/* Salary, Employer, Contact */}
        <div style={styles.formRow}>
          <FormField label="Current Salary:" width="100%"  value={personalContact.salary}  />
          <FormField label="Employer Name:" width="100%"  value={personalContact.employerName} />
          <FormField label="Contact #:" width="100%"  value={personalContact.phone} />
        </div>
        
        {/* Vehicle Information */}
        <div style={styles.formRow}>
          <div style={{ flex: '1 1 0', justifyContent: 'flex-start', alignItems: 'flex-end', gap: 16, display: 'flex' }}>
            <div style={{ ...styles.fieldLabel, fontWeight: 700 }}>
              Do you have a vehicle that you will use during your?
            </div>
            <div style={styles.checkboxGroup}>
              <CheckboxField label="Yes" checked={personalContact.hasVehicle} />
              <CheckboxField label="No" checked={!personalContact.hasVehicle} />
            </div>
          </div>
        </div>
        
        {/* Vehicle Details Table */}
        <div style={{ width: '100%' }}>
          <TableRow 
            columns={[
              { label: 'Make', key: 'make', uppercase: true },
              { label: 'model', key: 'model', uppercase: true },
              { label: 'TAG #', key: 'tag', uppercase: true },
              { label: 'INSURANCE', key: 'insurance' }
            ]}
            data={[
              { make: personalContact?.vehicleMake, model: personalContact?.vehicleModel, tag: personalContact?.licensePlate, insurance: personalContact?.insurance },
            ]}
            rows={1}
          />
        </div>
        
        {/* Insurance Requirement Note */}
        <div style={styles.noteText}>
          It is required that you provide valid copy of your vehicle registration and valid Insurance
        </div>
        
        {/* Legal Status */}
        <div style={styles.checkboxSection}>
          <div style={styles.sectionLabel}>Are you on any of the following:</div>
          <div style={styles.checkboxRow}>
            <CheckboxField label="Disability" checked={medicalLegal.disabilityBenefits} />
            <CheckboxField label="Community Control" />
            <CheckboxField label="House Arrest" />
            <CheckboxField label="Probation" checked={medicalLegal.probationStatus} />
            <CheckboxField label="N/A" checked={!medicalLegal.probationStatus && !medicalLegal.disabilityBenefits} />
          </div>
        </div>
      </div>

      {/* ACKNOWLEDGMENT SECTION */}
      <div style={styles.acknowledgmentSection}>
        <div style={styles.acknowledgmentHeader}>I acknowledge the following:</div>
        <div style={styles.acknowledgmentText}>
          Rent is due weekly in the amount of $125, failure to pay could result in eviction.<br/>
          I am making a 90-day commitment to Legacy Healing Center Margate and forgo my deposit if I do not remain at Legacy for the 90-day commitment period – for any reason<br/><br/>
          In the event of a relapse, I forgo my remaining rent for month and deposit <br/><br/>
          I am required to attend AA or NA meetings at least four times weekly<br/><br/>
          I will actively seek gainful employment and to accept and follow the rules and regulations set forth by Legacy.
        </div>
        <div style={styles.commitmentStatement}>
          By signing this document, I commit to adhere to the above mentioned.
        </div>
        <div
        style={{
          marginTop: "30px",
          display: "flex",
          justifyContent: "space-between",
        }}
      >
        <div style={{ textAlign: "center", flex: 1 }}>
          <div style={{ marginBottom: "30px" }}>
            <img
              src={signatureData || ""}
              alt="Client Signature"
              style={{
                width: "200px",
                height: "70px",
                borderBottom: "1px solid black",
              }}
            />
            <p>
              <span>Client Signature:</span>
            </p>
          </div>
        </div>
        <div style={{ textAlign: "center", flex: 1 }}>
          <div style={{ marginBottom: "20px", marginTop: signatureData ? "60px" : "0", }}>
            {/* {!currentDate && ( */}
            <div>{currentDate}</div>
            <div
              style={{
                borderBottom: "1px solid black",
                width: "300px",
                // height: "30px",
              }}
            ></div>
            {/* )} */}
            <p>Date:</p>
          </div>
        </div>
      </div>
      </div>
    </div>
  );
};

// Styles would remain the same as in your original component

// Styles
const styles = {
  container: {
    fontFamily: "Arial",
    padding: "20px",
    maxWidth: "800px",
    margin: "0 auto",
    lineHeight: "1.2",
    height: "2400px",
    fontSize: "20px"
  },
  section: {
    alignSelf: 'stretch',
    flexDirection: 'column',
    justifyContent: 'flex-start',
    alignItems: 'flex-start',
    gap: 20,
    display: 'flex'
  },
  sectionHeader: {
    alignSelf: 'stretch',
    textAlign: 'center',
    color: 'black',
    // fontSize: 16,
    fontFamily: 'Times New Roman',
    fontWeight: 400,
    textDecoration: 'underline',
    wordWrap: 'break-word'
  },
  formGroup: {
    alignSelf: 'stretch',
    flexDirection: 'column',
    justifyContent: 'flex-start',
    alignItems: 'flex-start',
    gap: 8,
    display: 'flex'
  },
  formRow: {
    alignSelf: 'stretch',
    justifyContent: 'flex-start',
    alignItems: 'flex-start',
    gap: 24,
    display: 'inline-flex'
  },
  fullWidthField: {
    alignSelf: 'stretch',
    height: 17,
    justifyContent: 'flex-start',
    alignItems: 'flex-end',
    gap: 4,
    display: 'inline-flex',
    marginTop: "30px"
  },
  fieldLabel: {
    textAlign: 'center',
    color: 'black',
    // fontSize: 12,
    fontFamily: 'Calibri',
    fontWeight: 400,
    wordWrap: 'break-word'
  },
  checkboxGroup: {
    justifyContent: 'flex-start',
    alignItems: 'center',
    gap: 8,
    display: 'flex'
  },
  checkboxLabel: {
    textAlign: 'center',
    color: 'black',
    // fontSize: 12,
    fontFamily: 'Calibri',
    fontWeight: 300,
    wordWrap: 'break-word'
  },
  checkbox: {
    width: 16,
    height: 16,
    border: '1px black solid'
  },
  tableRow: {
    alignSelf: 'stretch',
    overflow: 'hidden',
    justifyContent: 'flex-start',
    alignItems: 'flex-start',
    display: 'inline-flex'
  },
  tableCell: {
    alignSelf: 'stretch',
    paddingLeft: 12,
    paddingRight: 12,
    paddingTop: 4,
    paddingBottom: 4,
    border: '1px black solid',
    borderTop: 'none',
    borderRight: 'none',
    flexDirection: 'column',
    justifyContent: 'flex-start',
    alignItems: 'flex-start',
    display: 'inline-flex',
    color: 'black',
    // fontSize: 12,
    fontFamily: 'Calibri',
    wordWrap: 'break-word'
  },
  noteText: {
    color: 'black',
    // fontSize: 12,
    fontFamily: 'Calibri',
    fontWeight: 700,
    wordWrap: 'break-word'
  },
  checkboxSection: {
    alignSelf: 'stretch',
    flexDirection: 'column',
    justifyContent: 'flex-end',
    alignItems: 'flex-start',
    gap: 8,
    display: 'flex'
  },
  sectionLabel: {
    alignSelf: 'stretch',
    color: 'black',
    // fontSize: 12,
    fontFamily: 'Calibri',
    fontWeight: 700,
    wordWrap: 'break-word'
  },
  checkboxRow: {
    alignSelf: 'stretch',
    justifyContent: 'space-between',
    alignItems: 'center',
    display: 'inline-flex'
  },
  acknowledgmentSection: {
    width: '100%',
    height: '100%',
    flexDirection: 'column',
    justifyContent: 'flex-start',
    alignItems: 'flex-start',
    gap: 16,
    display: 'inline-flex'
  },
  acknowledgmentHeader: {
    color: 'black',
    // fontSize: 12,
    fontFamily: 'Calibri',
    fontWeight: '700',
    wordWrap: 'break-word'
  },
  acknowledgmentText: {
    alignSelf: 'stretch',
    paddingLeft: 16,
    paddingRight: 16,
    color: 'black',
    // fontSize: 12,
    fontFamily: 'Calibri',
    fontWeight: 400,
    wordWrap: 'break-word'
  },
  commitmentStatement: {
    color: 'black',
    // fontSize: 12,
    fontFamily: 'Calibri',
    fontWeight: '700',
    wordWrap: 'break-word'
  },
  signatureRow: {
    alignSelf: 'stretch',
    paddingLeft: 16,
    paddingRight: 16,
    justifyContent: 'flex-start',
    alignItems: 'center',
    gap: 80,
    display: 'inline-flex'
  },
  signatureLabel: {
    alignSelf: 'stretch',
    color: 'black',
    // fontSize: 12,
    fontFamily: 'Times New Roman',
    fontWeight: 400,
    wordWrap: 'break-word'
  }
};

LeaseApplication.propTypes = {
  // Add prop types if needed
};

export default LeaseApplication;